import React, { forwardRef, useImperativeHandle, useMemo, useState } from 'react';
import { Col, DatePicker, Form, Input, InputNumber, Row, Select } from 'antd';
import Modal from 'components/Modal';
import styled from 'styled-components';
import UploadImage from './components/UploadImage';
import { IProductCategoryItem } from 'services/shop/types/product';
import CheckboxGreen from '../../../CheckboxGreen';
import shopSelectors from 'services/shop/selectors';
import dayjs, { Dayjs } from 'dayjs';
import CurrencyInputPrecision from 'components/CurrencyInputPrecision';
import toast from 'utils/toast';
import { createProduct, updateProduct } from 'features/settingService/services/apis';
import { IProductBody } from 'features/settingService/services/types/api';
import useGetSupplier from 'features/settingService/services/hooks/useGetSupplier';
import { IResSupplier } from 'features/settingService/services/types/supplier';
import shopActions from 'services/shop/actions';
import { useAppDispatch } from 'store/hooks';
import { IResponseDataBody } from 'services/response';
import { IResponseDataBodyProduct } from 'features/settingService/services/types/product';
interface Props {}

type OpenData = {
  id?: string;
  catId?: string;
  productName?: string;
  urlImage?: string;
  priceSell?: number;
  commission?: number;
  unit?: number;
  orderNumber?: number;
  productCode?: string;
  image?: string | null;
  supplierId?: string | null;
  colorCode?: string | null;
  capacity?: string | null;
  description?: string | null;
  expiredDate?: string | null;
  availableSale?: boolean;
};
export interface IModalAddOrUpdateProductRef {
  open: (data?: OpenData) => void;
}

type FormType = {
  urlImage: string | null;
  availableSale: boolean;
  productName: string;
  catId: string | null;
  productCode: string;
  priceSell: number;
  unit: number;
  supplierId: string | null;
  capacity: string | null;
  colorCode: string | null;
  expiredDate: Dayjs | null;
  description: string | null;
};

const initialValues: FormType = {
  urlImage: null,
  productName: '',
  availableSale: false,
  catId: null,
  productCode: '',
  priceSell: 0,
  unit: 0,
  supplierId: null,
  capacity: null,
  colorCode: null,
  expiredDate: null,
  description: null,
};

const AddOrUpdateProductModal = forwardRef<IModalAddOrUpdateProductRef, Props>((_, ref) => {
  const [form] = Form.useForm<FormType>();
  const [visible, setVisible] = useState(false);
  const [data, setData] = useState<OpenData | null>(null);
  const dispatch = useAppDispatch();
  const categories = shopSelectors.data.productCategories();
  const [loading, setLoading] = useState(false);

  const { data: suppliers, getSuppliers } = useGetSupplier(true);

  const suppliersOptions = useMemo(() => {
    return (
      suppliers?.map((supplier: IResSupplier) => ({
        label: supplier.name,
        value: supplier.id,
      })) || []
    );
  }, [suppliers]);

  const catsOptions = useMemo(
    () =>
      categories.map((category: IProductCategoryItem) => ({
        label: category.categoryName,
        value: category.id,
      })),
    [categories]
  );

  const handleClose = () => {
    setVisible(false);
    setData(null);
    form.resetFields();
  };

  const handleSubmit = () => {
    form.submit();
  };

  const onFinish = async (values: FormType) => {
    try {
      setLoading(true);
      const body: IProductBody = {
        ...(data?.id ? { id: data?.id } : {}),
        urlImage: values?.urlImage ?? '',
        availableSale: values.availableSale,
        productName: values.productName,
        productCode: values.productCode ?? '',
        catId: values?.catId ?? '',
        priceSell: values.priceSell,
        unit: values.unit,
        supplierId: values.supplierId ?? '',
        capacity: values.capacity ?? '',
        colorCode: values.colorCode ?? '',
        expiredDate: values.expiredDate ? dayjs(values.expiredDate).format('MM-DD-YYYY') : '',
        description: values.description ?? '',
      };
      const res: IResponseDataBody<IResponseDataBodyProduct> = data?.id
        ? await updateProduct(body)
        : await createProduct(body);
      if (res?.data?.data) {
        if (data?.id) {
          dispatch(shopActions.get.updateProduct(res?.data?.data));
        } else {
          dispatch(shopActions.get.createProduct(res?.data?.data));
        }
        toast.success('Product created successfully');
        form.resetFields();
        handleClose();
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  useImperativeHandle(ref, () => ({
    open: (data?: OpenData) => {
      setVisible(true);
      getSuppliers({ page: 1, size: 100 });
      if (data) {
        setData(data);
        const dataSetForm = {
          ...data,
          expiredDate: data.expiredDate ? dayjs(data.expiredDate) : null,
        };
        form.setFieldsValue(dataSetForm);
      }
    },
  }));

  return (
    <Modal
      v2
      noneBodyStyle
      width={'80vw'}
      visible={visible}
      onClose={handleClose}
      onSubmit={handleSubmit}
      disableOk={loading}
      modalTitle={data?.id ? 'Update Product' : 'New Product'}
      okTitle='OK'
    >
      <Form form={form} onFinish={onFinish} initialValues={initialValues} layout='vertical'>
        <Wrapper>
          <Left>
            <Form.Item name='urlImage' noStyle>
              <UploadImage />
            </Form.Item>
          </Left>
          <Right>
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Form.Item name='availableSale' noStyle>
                  <CheckboxGreen label='Available for Sale checkbox' />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name='productName'
                  label={
                    <span>
                      Product name<span className='required'>*</span>
                    </span>
                  }
                  rules={[{ required: true, message: 'Please enter product name' }]}
                >
                  <Input placeholder='Please enter...' size='large' />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name='catId'
                  label={
                    <span>
                      Category<span className='required'>*</span>
                    </span>
                  }
                  rules={[{ required: true, message: 'Please select category' }]}
                >
                  <Select placeholder='Please select...' size='large' options={catsOptions} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name='productCode' label='SKU'>
                  <Input placeholder='Please enter...' suffix={<IcScan />} size='large' />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name='priceSell'
                  label={
                    <span>
                      Price<span className='required'>*</span>
                    </span>
                  }
                  rules={[
                    { required: true, message: 'Please enter price' },
                    {
                      validator: (_, value) => {
                        if (value <= 0) {
                          return Promise.reject(new Error('Price must be greater than 0'));
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <CurrencyInputPrecision size='large' />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name='unit'
                  label={
                    <span>
                      Unit<span className='required'>*</span>
                    </span>
                  }
                  rules={[{ required: true, message: 'Please enter unit' }]}
                >
                  <InputNumber
                    placeholder='Please enter...'
                    size='large'
                    min={0}
                    style={{ width: '100%', height: '4rem' }}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name='supplierId' label='Supplier'>
                  <Select placeholder='Please enter...' size='large' options={suppliersOptions} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name='capacity' label='Capacity'>
                  <Input placeholder='Please enter...' size='large' />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name='colorCode' label='Color Code'>
                  <Input placeholder='Please enter...' size='large' />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name='expiredDate' label='Expires date'>
                  <DatePicker placeholder='Please select...' size='large' />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item name='description' label='Note'>
                  <Input.TextArea placeholder='Please enter...' size='large' rows={4} />
                </Form.Item>
              </Col>
            </Row>
          </Right>
        </Wrapper>
      </Form>
    </Modal>
  );
});

AddOrUpdateProductModal.displayName = 'AddOrUpdateProductModal';
export default AddOrUpdateProductModal;

const Wrapper = styled.div`
  display: flex;
  min-height: 600px;
  .ant-form-item {
    margin-bottom: 0 !important;
  }
  .ant-input-number-input-wrap > input {
    height: 64px;
  }
  .ant-form-item-label label {
    font-size: 18px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
  }
  .required {
    margin-left: 4px;
    color: #d2464f;
    font-size: 14px;
    font-weight: 600;
  }
`;

const Left = styled.div`
  display: flex;
  padding: 0 24px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  background: #f5f5f5;
  box-shadow: 4px 0 8px 0 rgba(0, 0, 0, 0.15);
`;

const Right = styled.div`
  display: flex;
  padding: 16px;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  flex: 1 0 0;
`;

const IcScan = () => (
  <svg xmlns='http://www.w3.org/2000/svg' width='28' height='28' viewBox='0 0 28 28' fill='none'>
    <path
      d='M5.07042 4.86107H11.7371V11.5277H5.07042V4.86107ZM22.8482 4.86107V11.5277H16.1815V4.86107H22.8482ZM16.1815 17.0833H18.4038V14.8611H16.1815V12.6388H18.4038V14.8611H20.626V12.6388H22.8482V14.8611H20.626V17.0833H22.8482V20.4166H20.626V22.6388H18.4038V20.4166H15.0704V22.6388H12.8482V18.1944H16.1815V17.0833ZM18.4038 17.0833V20.4166H20.626V17.0833H18.4038ZM5.07042 22.6388V15.9722H11.7371V22.6388H5.07042ZM7.29264 7.08329V9.30552H9.51487V7.08329H7.29264ZM18.4038 7.08329V9.30552H20.626V7.08329H18.4038ZM7.29264 18.1944V20.4166H9.51487V18.1944H7.29264ZM5.07042 12.6388H7.29264V14.8611H5.07042V12.6388ZM10.626 12.6388H15.0704V17.0833H12.8482V14.8611H10.626V12.6388ZM12.8482 7.08329H15.0704V11.5277H12.8482V7.08329ZM2.8482 2.63885V7.08329H0.625977V2.63885C0.625977 2.04948 0.860103 1.48425 1.27685 1.0675C1.6936 0.650752 2.25883 0.416626 2.8482 0.416626L7.29264 0.416626V2.63885H2.8482ZM25.0704 0.416626C25.6598 0.416626 26.225 0.650752 26.6418 1.0675C27.0585 1.48425 27.2926 2.04948 27.2926 2.63885V7.08329H25.0704V2.63885H20.626V0.416626H25.0704ZM2.8482 20.4166V24.8611H7.29264V27.0833H2.8482C2.25883 27.0833 1.6936 26.8492 1.27685 26.4324C0.860103 26.0157 0.625977 25.4504 0.625977 24.8611V20.4166H2.8482ZM25.0704 24.8611V20.4166H27.2926V24.8611C27.2926 25.4504 27.0585 26.0157 26.6418 26.4324C26.225 26.8492 25.6598 27.0833 25.0704 27.0833H20.626V24.8611H25.0704Z'
      fill='#232F3E'
    />
  </svg>
);
